import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp,
  DollarSign,
  Users,
  Mail,
  BarChart3,
  Target,
  Award,
  Activity,
  Eye,
  MessageSquare,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Plus,
  Search,
  ChevronDown,
  Play,
  Pause,
  CheckCircle,
  Clock,
  Sparkles,
  Zap,
  Building,
  PieChart,
  LineChart,
  MousePointer,
  Reply,
  Trash2,
  AlertTriangle,
  Globe,
  Smartphone,
  Monitor,
  MapPin,
  Phone
} from 'lucide-react';
import { Button } from '../components/ui/Button/Button';
import { cn } from '../utils/cn';
import SMSAnalyticsDashboard from '../features/analytics/SMSAnalyticsDashboard';
import VoicemailAnalyticsDashboard from '../features/analytics/VoicemailAnalyticsDashboard';
import { CampaignAnalyticsDashboard } from '../features/analytics/CampaignAnalyticsDashboard';
import { ContactAnalyticsDashboard } from '../features/analytics/ContactAnalyticsDashboard';

// Analytics interfaces
interface AnalyticsTab {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
}

interface AnalyticsStats {
    totalRevenue: number;
    totalContacts: number;
    avgOpenRate: number;
    avgReplyRate: number;
  totalCampaigns: number;
  activeCampaigns: number;
  conversionRate: number;
  monthlyGrowth: number;
}

interface EmailAnalyticsStats {
  totalEmailsSent: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  replyRate: number;
  bounceRate: number;
  unsubscribeRate: number;
  engagementScore: number;
}

interface RevenueData {
  month: string;
  actual: number;
  target: number;
}

interface IndustryData {
  name: string;
  customers: number;
  revenue: number;
  avgValue: number;
  rank: number;
}

interface CampaignPerformance {
    id: string;
    name: string;
  type: 'email' | 'linkedin' | 'multi-channel';
  status: 'active' | 'paused' | 'completed';
    sent: number;
    opened: number;
    clicked: number;
    replied: number;
    revenue: number;
  openRate: number;
  replyRate: number;
  conversionRate: number;
}

interface EmailCampaignData {
  id: string;
  name: string;
  subject: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  unsubscribed: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  replyRate: number;
  bounceRate: number;
  createdAt: string;
}

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; prefix?: string; suffix?: string; decimals?: number }> = ({ 
  end, 
  duration = 2000, 
  prefix = "", 
  suffix = "",
  decimals = 0
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const currentCount = progress * end;
      setCount(decimals > 0 ? Number(currentCount.toFixed(decimals)) : Math.floor(currentCount));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, decimals]);

  return (
    <span className="font-bold">
      {prefix}{decimals > 0 ? count.toFixed(decimals) : count.toLocaleString()}{suffix}
    </span>
  );
};

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  icon: React.ElementType;
  gradient: string;
  description?: string;
  decimals?: number;
  prefix?: string;
  suffix?: string;
}> = ({ title, value, change, icon: Icon, gradient, description, decimals = 0, prefix = "", suffix = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} decimals={decimals} prefix={prefix} suffix={suffix} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">{change}</span>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
          </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

// Mini Chart Component
const MiniChart: React.FC<{ data: number[]; color: string }> = ({ data, color }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min;
  
  return (
    <div className="h-12 flex items-end space-x-1">
      {data.map((value, index) => {
        const height = range > 0 ? ((value - min) / range) * 100 : 50;
        return (
          <div
            key={index}
            className={cn("w-1.5 rounded-sm transition-all duration-500", color)}
            style={{ height: `${Math.max(height, 8)}%` }}
          />
        );
      })}
    </div>
  );
};

// Revenue Trend Chart Component
const RevenueTrendChart: React.FC<{ data: RevenueData[] }> = ({ data }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Revenue Trend</h3>
          <p className="text-neutral-400 text-sm">Monthly performance vs targets</p>
        </div>
        <Button variant="outline" size="sm" className="text-neutral-300 border-neutral-600">
          <Calendar className="w-4 h-4 mr-2" />
          Last 6 months
        </Button>
      </div>
      
      <div className="h-64 flex items-end justify-between space-x-2">
        {data.map((item, index) => {
          const maxRevenue = Math.max(...data.map(d => Math.max(d.actual, d.target)));
          const actualHeight = (item.actual / maxRevenue) * 100;
          const targetHeight = (item.target / maxRevenue) * 100;
          
          return (
            <div key={index} className="flex-1 flex flex-col items-center space-y-2">
              <div className="w-full flex justify-center space-x-1 h-48">
                <div 
                  className="w-4 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all duration-1000 ease-out"
                  style={{ height: `${actualHeight}%` }}
                />
                <div 
                  className="w-4 bg-gradient-to-t from-neutral-600 to-neutral-500 rounded-t transition-all duration-1000 ease-out"
                  style={{ height: `${targetHeight}%` }}
                />
              </div>
              <div className="text-center">
                <div className="text-xs font-medium text-white">{item.month}</div>
                <div className="text-xs text-neutral-400">
                  ${(item.actual / 1000).toFixed(0)}k
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-neutral-700">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
          <span className="text-sm text-neutral-400">Actual Revenue</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-neutral-500 rounded-full"></div>
          <span className="text-sm text-neutral-400">Target</span>
        </div>
      </div>
    </motion.div>
  );
};

// Top Industries Component
const TopIndustries: React.FC<{ data: IndustryData[] }> = ({ data }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Top Industries by Revenue</h3>
          <p className="text-neutral-400 text-sm">Performance breakdown by sector</p>
        </div>
      </div>
      
      <div className="space-y-4">
        {data.map((industry, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center justify-between p-4 bg-neutral-800/30 rounded-xl hover:bg-neutral-800/50 transition-colors"
          >
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                {industry.rank}
              </div>
              <div>
                <h4 className="text-white font-medium">{industry.name}</h4>
                <p className="text-neutral-400 text-sm">{industry.customers} customers</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-white font-semibold">
                ${industry.revenue.toLocaleString()}
              </div>
              <div className="text-neutral-400 text-sm">
                Avg: ${industry.avgValue.toLocaleString()}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

// Email Campaign Table Component
const EmailCampaignTable: React.FC<{ campaigns: EmailCampaignData[] }> = ({ campaigns }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-white mb-1">Email Campaign Performance</h3>
          <p className="text-neutral-400 text-sm">Detailed metrics for all email campaigns</p>
        </div>
        <Button variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-700">
              <th className="text-left text-neutral-400 font-medium py-3 px-4">Campaign</th>
              <th className="text-left text-neutral-400 font-medium py-3 px-4">Sent</th>
              <th className="text-left text-neutral-400 font-medium py-3 px-4">Delivered</th>
              <th className="text-left text-neutral-400 font-medium py-3 px-4">Opened</th>
              <th className="text-left text-neutral-400 font-medium py-3 px-4">Clicked</th>
              <th className="text-left text-neutral-400 font-medium py-3 px-4">Replied</th>
              <th className="text-left text-neutral-400 font-medium py-3 px-4">Open Rate</th>
              <th className="text-left text-neutral-400 font-medium py-3 px-4">CTR</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign, index) => (
                <motion.tr
                  key={campaign.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border-b border-neutral-800/50 hover:bg-neutral-800/30 transition-colors"
              >
                <td className="py-4 px-4">
                      <div>
                    <div className="text-white font-medium">{campaign.name}</div>
                    <div className="text-neutral-400 text-sm truncate max-w-xs">{campaign.subject}</div>
                    </div>
                  </td>
                <td className="py-4 px-4 text-white">{campaign.sent.toLocaleString()}</td>
                <td className="py-4 px-4 text-white">{campaign.delivered.toLocaleString()}</td>
                <td className="py-4 px-4 text-white">{campaign.opened.toLocaleString()}</td>
                <td className="py-4 px-4 text-white">{campaign.clicked.toLocaleString()}</td>
                <td className="py-4 px-4 text-white">{campaign.replied.toLocaleString()}</td>
                <td className="py-4 px-4">
                  <span className={cn(
                    "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                    campaign.openRate >= 25 ? "bg-green-500/20 text-green-400" :
                    campaign.openRate >= 15 ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-red-500/20 text-red-400"
                  )}>
                    {campaign.openRate.toFixed(1)}%
                      </span>
                  </td>
                <td className="py-4 px-4">
                  <span className={cn(
                    "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                    campaign.clickRate >= 3 ? "bg-green-500/20 text-green-400" :
                    campaign.clickRate >= 1 ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-red-500/20 text-red-400"
                  )}>
                    {campaign.clickRate.toFixed(1)}%
                      </span>
                  </td>
                </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export const AnalyticsPage: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AnalyticsStats>({
    totalRevenue: 0,
    totalContacts: 0,
    avgOpenRate: 0.0,
    avgReplyRate: 0.0,
    totalCampaigns: 0,
    activeCampaigns: 0,
    conversionRate: 0.0,
    monthlyGrowth: 0.0
  });

  const [emailStats, setEmailStats] = useState<EmailAnalyticsStats>({
    totalEmailsSent: 0,
    deliveryRate: 0.0,
    openRate: 0.0,
    clickRate: 0.0,
    replyRate: 0.0,
    bounceRate: 0.0,
    unsubscribeRate: 0.0,
    engagementScore: 0.0
  });

  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [topIndustries, setTopIndustries] = useState<IndustryData[]>([]);
  const [emailCampaigns, setEmailCampaigns] = useState<EmailCampaignData[]>([]);

  const tabs: AnalyticsTab[] = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'contacts', label: 'Contact Analytics', icon: Users },
    { id: 'email', label: 'Email Analytics', icon: Mail, badge: 5 },
    { id: 'sms', label: 'SMS Analytics', icon: MessageSquare },
    { id: 'voicemail', label: 'Voicemail Analytics', icon: Phone },
    { id: 'social', label: 'Social Media', icon: Building },
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'campaigns', label: 'Campaigns', icon: Target }
  ];

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockStats: AnalyticsStats = {
        totalRevenue: 180000,
        totalContacts: 2847,
        avgOpenRate: 28.7,
        avgReplyRate: 9.4,
        totalCampaigns: 23,
        activeCampaigns: 8,
        conversionRate: 12.8,
        monthlyGrowth: 18.2
      };

      const mockEmailStats: EmailAnalyticsStats = {
        totalEmailsSent: 45230,
        deliveryRate: 97.8,
        openRate: 28.7,
        clickRate: 4.2,
        replyRate: 9.4,
        bounceRate: 2.2,
        unsubscribeRate: 0.8,
        engagementScore: 85.3
      };

      const mockRevenueData: RevenueData[] = [
        { month: 'Jan', actual: 120000, target: 100000 },
        { month: 'Feb', actual: 135000, target: 120000 },
        { month: 'Mar', actual: 165000, target: 140000 },
        { month: 'Apr', actual: 158000, target: 160000 },
        { month: 'May', actual: 192000, target: 170000 },
        { month: 'Jun', actual: 225000, target: 200000 }
      ];

      const mockTopIndustries: IndustryData[] = [
        { name: 'Technology', customers: 450, revenue: 180000, avgValue: 400, rank: 1 },
        { name: 'Healthcare', customers: 320, revenue: 120000, avgValue: 375, rank: 2 },
        { name: 'Finance', customers: 280, revenue: 95000, avgValue: 339, rank: 3 },
        { name: 'Manufacturing', customers: 190, revenue: 75000, avgValue: 395, rank: 4 }
      ];

      const mockEmailCampaigns: EmailCampaignData[] = [
        {
          id: 'camp-001',
          name: 'Q1 Product Launch',
          subject: 'Introducing our revolutionary new platform',
          sent: 5000,
          delivered: 4890,
          opened: 1223,
          clicked: 183,
          replied: 47,
          bounced: 110,
          unsubscribed: 8,
          deliveryRate: 97.8,
          openRate: 25.0,
          clickRate: 3.7,
          replyRate: 0.9,
          bounceRate: 2.2,
          createdAt: '2024-01-15'
        },
        {
          id: 'camp-002',
          name: 'Customer Success Stories',
          subject: 'See how TechCorp increased ROI by 300%',
          sent: 3200,
          delivered: 3136,
          opened: 1003,
          clicked: 94,
          replied: 31,
          bounced: 64,
          unsubscribed: 5,
          deliveryRate: 98.0,
          openRate: 32.0,
          clickRate: 3.0,
          replyRate: 1.0,
          bounceRate: 2.0,
          createdAt: '2024-01-20'
        },
        {
          id: 'camp-003',
          name: 'Weekly Newsletter',
          subject: 'Industry insights and trends - Week 4',
          sent: 8500,
          delivered: 8245,
          opened: 2144,
          clicked: 257,
          replied: 68,
          bounced: 255,
          unsubscribed: 12,
          deliveryRate: 97.0,
          openRate: 26.0,
          clickRate: 3.1,
          replyRate: 0.8,
          bounceRate: 3.0,
          createdAt: '2024-01-22'
        }
      ];

      setStats(mockStats);
      setEmailStats(mockEmailStats);
      setRevenueData(mockRevenueData);
      setTopIndustries(mockTopIndustries);
      setEmailCampaigns(mockEmailCampaigns);
    } catch (error) {
      console.error('Failed to load analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
    return (
          <div className="space-y-8">
            {/* Stats Cards */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-32 bg-neutral-800/50 rounded-2xl animate-pulse" />
              ))}
            </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                <MetricCard
                  title="Total Revenue"
                  value={stats.totalRevenue}
                  change="+18.2% this month"
                  icon={DollarSign}
                  gradient="bg-gradient-to-r from-green-500 to-emerald-500"
                  description="All campaigns combined"
                  prefix="$"
                />
                <MetricCard
                  title="Total Contacts"
                  value={stats.totalContacts}
                  change="+12.5% vs last month"
                  icon={Users}
                  gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
                  description="Across all campaigns"
                />
                <MetricCard
                  title="Avg Open Rate"
                  value={stats.avgOpenRate}
                  change="+3.1% improvement"
                  icon={Eye}
                  gradient="bg-gradient-to-r from-purple-500 to-pink-500"
                  description="Last 30 days"
                  suffix="%"
                  decimals={1}
                />
                <MetricCard
                  title="Avg Reply Rate"
                  value={stats.avgReplyRate}
                  change="+1.8% vs industry"
                  icon={MessageSquare}
                  gradient="bg-gradient-to-r from-orange-500 to-red-500"
                  description="Email campaigns only"
                  suffix="%"
                  decimals={1}
                />
              </motion.div>
            )}

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <RevenueTrendChart data={revenueData} />
              <TopIndustries data={topIndustries} />
            </div>

            {/* Additional Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <MetricCard
                title="Active Campaigns"
                value={stats.activeCampaigns}
                change="Currently running"
                icon={Activity}
                gradient="bg-gradient-to-r from-indigo-500 to-purple-500"
                description={`${stats.totalCampaigns} total campaigns`}
              />
              <MetricCard
                title="Conversion Rate"
                value={stats.conversionRate}
                change="+2.4% this quarter"
                icon={Target}
                gradient="bg-gradient-to-r from-teal-500 to-cyan-500"
                description="Lead to customer"
                suffix="%"
                decimals={1}
              />
              <MetricCard
                title="Monthly Growth"
                value={stats.monthlyGrowth}
                change="Accelerating"
                icon={TrendingUp}
                gradient="bg-gradient-to-r from-pink-500 to-rose-500"
                description="Revenue growth rate"
                suffix="%"
                decimals={1}
              />
              <MetricCard
                title="Quality Score"
                value="92"
                change="+5% vs last week"
                icon={Award}
                gradient="bg-gradient-to-r from-yellow-500 to-orange-500"
                description="Content quality average"
                suffix="%"
              />
            </motion.div>
      </div>
    );

      case 'email':
  return (
          <div className="space-y-8">
            {/* Email Stats Cards */}
        <motion.div
              initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <MetricCard
                title="Emails Sent"
                value={emailStats.totalEmailsSent}
                change="+15.3% this month"
                icon={Mail}
                gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
                description="Total email volume"
              />
              <MetricCard
                title="Delivery Rate"
                value={emailStats.deliveryRate}
                change="+0.8% improvement"
                icon={CheckCircle}
                gradient="bg-gradient-to-r from-green-500 to-emerald-500"
                description="Successfully delivered"
                suffix="%"
                decimals={1}
              />
              <MetricCard
                title="Open Rate"
                value={emailStats.openRate}
                change="+2.1% vs last month"
                icon={Eye}
                gradient="bg-gradient-to-r from-purple-500 to-pink-500"
                description="Email opens"
                suffix="%"
                decimals={1}
              />
              <MetricCard
                title="Click Rate"
                value={emailStats.clickRate}
                change="+0.5% improvement"
                icon={MousePointer}
                gradient="bg-gradient-to-r from-orange-500 to-red-500"
                description="Link clicks"
                suffix="%"
                decimals={1}
              />
        </motion.div>

            {/* Secondary Email Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
            <MetricCard
                title="Reply Rate"
                value={emailStats.replyRate}
                change="+1.2% vs industry"
                icon={Reply}
                gradient="bg-gradient-to-r from-indigo-500 to-purple-500"
                description="Email responses"
                suffix="%"
                decimals={1}
            />
            <MetricCard
                title="Bounce Rate"
                value={emailStats.bounceRate}
                change="-0.3% improvement"
                icon={AlertTriangle}
                gradient="bg-gradient-to-r from-yellow-500 to-orange-500"
                description="Failed deliveries"
                suffix="%"
                decimals={1}
            />
            <MetricCard
                title="Unsubscribe Rate"
                value={emailStats.unsubscribeRate}
                change="-0.1% better"
                icon={Trash2}
                gradient="bg-gradient-to-r from-red-500 to-pink-500"
                description="List churn"
                suffix="%"
                decimals={1}
            />
            <MetricCard
                title="Engagement Score"
                value={emailStats.engagementScore}
                change="+3.2% this week"
                icon={Zap}
                gradient="bg-gradient-to-r from-teal-500 to-cyan-500"
                description="Overall engagement"
                suffix=""
                decimals={1}
              />
        </motion.div>

            {/* Real-time Analytics & A/B Testing */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Real-time Email Activity */}
          <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">Real-time Activity</h3>
                    <p className="text-neutral-400 text-sm">Live email engagement updates</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-green-400 text-sm">Live</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {[
                    { action: 'Email Opened', campaign: 'Q1 Product Launch', time: '2 seconds ago', user: 'john@techcorp.com' },
                    { action: 'Link Clicked', campaign: 'Customer Stories', time: '15 seconds ago', user: 'sarah@startup.io' },
                    { action: 'Reply Received', campaign: 'Newsletter', time: '1 minute ago', user: 'mike@company.com' },
                    { action: 'Email Opened', campaign: 'Follow-up Series', time: '2 minutes ago', user: 'lisa@enterprise.net' }
                  ].map((activity, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-neutral-800/30 rounded-lg">
                      <div>
                  <div className="flex items-center space-x-2">
                          <div className={cn(
                            "w-2 h-2 rounded-full",
                            activity.action === 'Reply Received' ? 'bg-green-400' :
                            activity.action === 'Link Clicked' ? 'bg-blue-400' :
                            'bg-purple-400'
                          )}></div>
                          <span className="text-white font-medium text-sm">{activity.action}</span>
                  </div>
                        <p className="text-neutral-400 text-xs">{activity.campaign} • {activity.user}</p>
                </div>
                      <span className="text-neutral-500 text-xs">{activity.time}</span>
                    </div>
                  ))}
                </div>
          </motion.div>

              {/* A/B Testing Results */}
          <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">A/B Testing Results</h3>
                    <p className="text-neutral-400 text-sm">Subject line performance comparison</p>
                  </div>
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    New Test
                  </Button>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 bg-neutral-800/30 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white font-medium">Current Test: Product Launch</span>
                      <span className="text-green-400 text-sm">95% confidence</span>
                        </div>
                    
                    <div className="space-y-3">
                        <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-neutral-300">Version A: "Introducing our new platform"</span>
                          <span className="text-green-400 font-medium">32.4%</span>
                          </div>
                        <div className="w-full bg-neutral-700 rounded-full h-2">
                          <div className="bg-green-400 h-2 rounded-full" style={{ width: '32.4%' }}></div>
                          </div>
                        </div>
                      
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-neutral-300">Version B: "Revolutionary platform launch"</span>
                          <span className="text-blue-400 font-medium">28.1%</span>
                      </div>
                        <div className="w-full bg-neutral-700 rounded-full h-2">
                          <div className="bg-blue-400 h-2 rounded-full" style={{ width: '28.1%' }}></div>
                        </div>
                        </div>
                      </div>
                    
                    <div className="mt-3 text-center">
                      <span className="text-green-400 text-sm font-medium">Winner: Version A (+4.3% lift)</span>
                    </div>
                </div>
                </div>
          </motion.div>
        </div>

            {/* Email Campaign Table */}
            <EmailCampaignTable campaigns={emailCampaigns} />

            {/* Advanced Analytics Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Device Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">Device Breakdown</h3>
                    <p className="text-neutral-400 text-sm">Email opens by device type</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {[
                    { device: 'Mobile', percentage: 68, icon: Smartphone, color: 'from-blue-500 to-cyan-500' },
                    { device: 'Desktop', percentage: 28, icon: Monitor, color: 'from-purple-500 to-pink-500' },
                    { device: 'Tablet', percentage: 4, icon: Monitor, color: 'from-green-500 to-emerald-500' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-r", item.color)}>
                          <item.icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-white font-medium">{item.device}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-24 h-2 bg-neutral-700 rounded-full overflow-hidden">
                          <div 
                            className={cn("h-full rounded-full bg-gradient-to-r transition-all duration-1000", item.color)}
                            style={{ width: `${item.percentage}%` }}
                          />
                </div>
                        <span className="text-white font-medium w-12 text-right">{item.percentage}%</span>
              </div>
                    </div>
                  ))}
                </div>
        </motion.div>

              {/* Engagement Patterns */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">Engagement Patterns</h3>
                    <p className="text-neutral-400 text-sm">Optimal send times</p>
                  </div>
                </div>
                
              <div className="space-y-4">
                  {[
                    { time: '9:00 AM', engagement: 85, day: 'Tuesday' },
                    { time: '2:00 PM', engagement: 78, day: 'Wednesday' },
                    { time: '11:00 AM', engagement: 72, day: 'Thursday' },
                    { time: '10:00 AM', engagement: 69, day: 'Monday' }
                  ].map((pattern, index) => (
                    <div key={index} className="p-3 bg-neutral-800/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{pattern.time} - {pattern.day}</span>
                        <span className="text-green-400 font-medium">{pattern.engagement}%</span>
                      </div>
                      <div className="w-full bg-neutral-700 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${pattern.engagement}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Deliverability Health */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
              >
                <div className="flex items-center justify-between mb-6">
                      <div>
                    <h3 className="text-xl font-semibold text-white mb-1">Deliverability Health</h3>
                    <p className="text-neutral-400 text-sm">Sender reputation metrics</p>
                        </div>
                        </div>
                
                <div className="space-y-4">
                  {[
                    { metric: 'Sender Score', value: 98, status: 'excellent', color: 'text-green-400' },
                    { metric: 'Domain Rep', value: 95, status: 'very good', color: 'text-green-400' },
                    { metric: 'IP Reputation', value: 92, status: 'good', color: 'text-blue-400' },
                    { metric: 'Spam Rate', value: 0.2, status: 'low', color: 'text-green-400', suffix: '%' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div>
                        <span className="text-white font-medium text-sm">{item.metric}</span>
                        <p className="text-neutral-400 text-xs capitalize">{item.status}</p>
                      </div>
                      <span className={cn("font-bold", item.color)}>
                        {item.value}{item.suffix || ''}
                      </span>
                    </div>
                  ))}
                        </div>

                <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm font-medium">Excellent deliverability health</span>
                      </div>
                        </div>
              </motion.div>
                      </div>

            {/* Geographic Performance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-1">Global Performance Map</h3>
                  <p className="text-neutral-400 text-sm">Email engagement by region with detailed insights</p>
                </div>
                <Button variant="outline" size="sm">
                  <Globe className="w-4 h-4 mr-2" />
                  View Full Map
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { region: 'North America', openRate: 32.1, clickRate: 4.8, replies: 156, growth: '+12%' },
                  { region: 'Europe', openRate: 28.9, clickRate: 3.9, replies: 124, growth: '+8%' },
                  { region: 'Asia Pacific', openRate: 25.7, clickRate: 3.2, replies: 89, growth: '+15%' },
                  { region: 'Latin America', openRate: 22.3, clickRate: 2.8, replies: 67, growth: '+22%' }
                ].map((region, index) => (
                  <div key={index} className="p-4 bg-neutral-800/30 rounded-xl hover:bg-neutral-800/50 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-blue-400" />
                        <span className="text-white font-medium text-sm">{region.region}</span>
                      </div>
                      <span className="text-green-400 text-xs font-medium">{region.growth}</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-neutral-400">Open Rate</span>
                        <span className="text-green-400">{region.openRate}%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-neutral-400">Click Rate</span>
                        <span className="text-blue-400">{region.clickRate}%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-neutral-400">Replies</span>
                        <span className="text-purple-400">{region.replies}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
        </motion.div>
      </div>
        );

      case 'contacts':
        return (
          <div className="space-y-6">
            <ContactAnalyticsDashboard 
              contactId="demo-contact-123" 
              contactName="John Smith"
              companyName="TechCorp Inc."
            />
          </div>
        );

      case 'sms':
        return (
          <div className="space-y-6">
            <SMSAnalyticsDashboard 
              campaignId="demo-sms-campaign" 
              className="bg-transparent"
            />
          </div>
        );

      case 'voicemail':
        return (
          <div className="space-y-6">
            <VoicemailAnalyticsDashboard 
              campaignId="demo-voicemail-campaign" 
              className="bg-transparent"
            />
      </div>
        );

      case 'social':
        return (
          <div className="text-center py-20">
            <Building className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-white mb-2">Social Media Analytics</h3>
            <p className="text-neutral-400">Coming soon - LinkedIn and social engagement metrics</p>
          </div>
        );

      case 'revenue':
        return (
          <div className="text-center py-20">
            <DollarSign className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-white mb-2">Revenue Analytics</h3>
            <p className="text-neutral-400">Coming soon - Detailed revenue attribution and forecasting</p>
          </div>
        );

      case 'campaigns':
        return (
          <div className="space-y-6">
            <CampaignAnalyticsDashboard className="bg-transparent" />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              Analytics & Insights
            </h1>
            <p className="text-xl text-neutral-400">
              Comprehensive performance analysis and detailed reporting
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
              {['7d', '30d', '90d', '1y'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range as any)}
                  className={cn(
                    'px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                    timeRange === range
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                  )}
                >
                  Last {range}
                </button>
              ))}
            </div>
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <Download className="w-5 h-5 mr-2" />
              Export Report
            </Button>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-4 shadow-xl">
            <div className="flex items-center space-x-2 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex items-center justify-between px-4 py-3 text-left rounded-xl transition-all duration-200 whitespace-nowrap',
                      isActive
                        ? 'bg-blue-500/90 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-800/60'
                    )}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{tab.label}</span>
                    </div>
                    {tab.badge && (
                      <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderTabContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AnalyticsPage; 